import type {
  BuyerParticipationQualificationResponseDto,
  BuyerParticipationRequestDetailDto,
  SelectionCriterionDto,
} from "../../../../autogen/bff-api";
import { composeCriterionKey } from "../../../../common/qualification/criterionKey";

export interface RequestQualificationProgress {
  fulfilled: number;
  notFulfilled: number;
  pending: number;
  reviewed: number;
  total: number;
  hasRequirements: boolean;
}

// Mirrors bidQualification.ts — progress counts criteria, not responses, so an unanswered
// criterion still counts as pending until the buyer reviews it.
export const getRequestQualificationProgress = (
  criteria: SelectionCriterionDto[],
  responses: BuyerParticipationQualificationResponseDto[],
): RequestQualificationProgress => {
  const responsesByKey = new Map(responses.map((r) => [r.criterionKey, r] as const));
  const verdicts = criteria.map((c) => responsesByKey.get(composeCriterionKey(c))?.buyerVerdict ?? null);
  const fulfilled = verdicts.filter((v) => v === true).length;
  const notFulfilled = verdicts.filter((v) => v === false).length;
  const pending = verdicts.length - fulfilled - notFulfilled;
  return {
    fulfilled,
    notFulfilled,
    pending,
    reviewed: fulfilled + notFulfilled,
    total: criteria.length,
    hasRequirements: criteria.length > 0,
  };
};

export interface RequestQualificationRow extends BuyerParticipationQualificationResponseDto {
  criterion: SelectionCriterionDto;
}

export const getRequestQualificationRows = (
  detail: BuyerParticipationRequestDetailDto,
): RequestQualificationRow[] => {
  const criteria = detail.selectionCriteria ?? [];
  const responsesByKey = new Map(
    (detail.request.qualificationResponses ?? []).map((r) => [r.criterionKey, r] as const),
  );
  return criteria.map((criterion) => {
    const key = composeCriterionKey(criterion);
    const response: BuyerParticipationQualificationResponseDto = responsesByKey.get(key) ?? {
      criterionKey: key,
      buyerVerdict: null,
      buyerComment: null,
    };
    return { ...response, criterion };
  });
};
